import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminLogin, setToken, getToken } from "../lib/api";
import Logo from "../components/Logo";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (getToken()) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminLogin(email, password);
      setToken(data.access_token);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-stone-50 flex flex-col">
      <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Logo size="default" />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex w-16 h-16 bg-amber-700 rounded-2xl items-center justify-center mb-6">
              <ShieldCheck className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-amber-700 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
              Care Team Access
            </p>
            <h1 className="font-serif text-4xl text-slate-900 tracking-tight">
              Admin login
            </h1>
            <p className="text-slate-600 mt-3">
              Sign in to view and manage inquiries.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 lg:p-10 border border-stone-200/70 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label className="text-slate-900 font-medium">Email</Label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email"
                    data-testid="admin-login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@blessingcompanioncare.com"
                    className="pl-12 py-6 rounded-xl border-stone-200 focus-visible:ring-amber-700"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-900 font-medium">Password</Label>
                <div className="relative">
                  <Lock className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="password"
                    data-testid="admin-login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-12 py-6 rounded-xl border-stone-200 focus-visible:ring-amber-700"
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="admin-login-submit"
                disabled={loading}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-full py-6 font-medium mt-2"
              >
                {loading ? (
                  "Signing in..."
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <p className="text-center mt-6 text-xs text-slate-500">
            Authorized personnel only. All access is logged.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
